import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './VCardGenerator.html?raw';
import lang from 'dojo/_base/lang';
import { AddressTypes } from "../_loadingHelpers/Address"

import _uiFrameMixin from '../../moreCore/UI/Container/uiFrame';
import Edit from '../../moreCore/UI/Edit/Edit';
import CheckBox from '../../moreCore/UI/CheckBox/CheckBox';
import MultiselectBox from '../../moreCore/UI/EntityBased/MultiselectBox/MultiselectBox';
import emailForm from '../EMail/emailForm';

class VCardGenerator extends compose(_uiFrameMixin) {
    templateString: any = template;

    private addressID: string;
    private addressData: AddressTypes.VCardAddressData;

    private selectAddressContainer: HTMLElement;
    private dataContainer: HTMLElement;
    private previewContainer: HTMLElement;

    private name: MdsLax<InstanceType<typeof Edit>>;
    private address: MdsLax<InstanceType<typeof Edit>>;
    private note: MdsLax<InstanceType<typeof Edit>>;
    private telecomm: MdsLax<InstanceType<typeof MultiselectBox>>;
    private checkbox_includeAddress: MdsLax<InstanceType<typeof CheckBox>>;
    private vCardString: any = null;

    private vCardBlob: Blob = null;
    private vCardFilename: any = null;

    constructor(ID: string) {
        super(...(arguments as unknown as []));
        this.addressID = ID;
    }

    private loadAddressData() {
        this.StandbyStart();

        this.RequestManager.callfunction(
            "Webtool_API__VCardGenerator_LoadAddressData",
            { addressID: this.addressID },
            lang.hitch(this, this.onLoadAddressDataResponse),
            lang.hitch(this, this._showErrorMessage)
        )
    }

    private onLoadAddressDataResponse(response: AddressTypes.VCardAddressData) {
        this.addressData = response;

        this.StandbyStop();
        this.setTitles(this.translate("AddressWizard__Label_VCardGenerator"));

        switch (response.Classname) {
            case "persons":
                this.name.setValue(response.gDisp_person, true);
                break;
            case "organisations_internal":
            case "organisations_external":
                this.name.setValue(response.gDisp_1, true);
                break;
            case "contact_persons":
            case "associates":
                this.name.setValue(response.ref_person.gDisp_1, true);
                break;
        }

        this.telecomm.setData(null, response.EL_vt_telecommunications_adress);
        this.telecomm.selectAll();

        this.address.setValue(`${response.street}\r\n${response.c_postal_code} ${response.c_city}\r\n${response.ref_country ? response.ref_country.description : ""}`.trim(), true);

        this.generateVCard();
    }

    private generateVCard() {
        let type = "";
        let vCardLines = ['BEGIN:VCARD', 'VERSION:3.0'];

        switch (this.addressData.Classname) {
            case "persons":
                type = "HOME";
                vCardLines.push(`FN;CHARSET=utf-8:${this.addressData.gDisp_person}`);

                let prefixes = [
                    this.addressData.salutation_short,
                    this.addressData.professional_title_short,
                    this.addressData.academic_title_short
                ].filter(Boolean).join(",");
                let suffixes = [
                    this.addressData.name_suffix,
                    this.addressData.academic_title_suffix_short
                ].filter(Boolean).join(",");

                vCardLines.push(`N;CHARSET=utf-8:${this.addressData.name};${this.addressData.name_first};${this.addressData.name_second};${prefixes};${suffixes}`);
                break;
            case "organisations_internal":
            case "organisations_external":
                type = "WORK";
                vCardLines.push(`FN;CHARSET=utf-8:${this.addressData.gDisp_1}`);
                vCardLines.push(`ORG;CHARSET=utf-8:${this.addressData.gDisp_1}`);
                break;
            case "contact_persons":
            case "associates":
                type = "WORK";
                vCardLines.push(`FN;CHARSET=utf-8:${this.addressData.ref_person.gDisp_1}`);
                vCardLines.push(`ORG;CHARSET=utf-8:${this.addressData.ref_organisation.gDisp_1};${this.addressData.ref_division ? this.addressData.ref_division.name : ""}`);
                vCardLines.push(`TITLE;CHARSET=utf-8:${this.addressData.c_job_title}`);
                break;
        }

        this.addressData.EL_vt_telecommunications_adress.forEach(telecomm => {
            let selectedList: string[] = this.telecomm.getValue();

            if (selectedList.indexOf(telecomm.ID) != -1) {
                switch (telecomm.Classname) {
                    case "fax_addresses":
                        vCardLines.push(`TEL;TYPE=${type},FAX${telecomm.is_main_connection ? ",PREF" : ""}:${telecomm.telecom_code.replace(" ", "-")}`);
                        break;
                    case "phone_numbers":
                        vCardLines.push(`TEL;TYPE=${type},VOICE${telecomm.is_main_connection ? ",PREF" : ""}:${telecomm.telecom_code}`);
                        break;
                    case "mobilephone_numbers":
                        vCardLines.push(`TEL;TYPE=${type},CELL${telecomm.is_main_connection ? ",PREF" : ""}:${telecomm.telecom_code}`);
                        break;
                    case "email_addresses":
                        vCardLines.push(`EMAIL;TYPE=INTERNET${telecomm.is_main_connection ? ",PREF" : ""}:${telecomm.telecom_code}`);
                        break;
                    case "messenger_addresses":
                    case "webphone_addresses":
                    case "web_addresses":
                        vCardLines.push(`URL${telecomm.is_main_connection ? ";TYPE=PREF" : ""}:${telecomm.telecom_code}`);
                        break;
                }
            }
        });

        if (this.checkbox_includeAddress.getValue() === true) {

            vCardLines.push(`ADR;CHARSET=utf-8;TYPE=${type}:;;${this.addressData.street};${this.addressData.c_city};;${this.addressData.c_postal_code};${this.addressData.ref_country ? this.addressData.ref_country.description : ""}`)
        }

        let noteVal = this.note.getValue();
        if (noteVal) {
            vCardLines.push(`NOTE;CHARSET=utf-8:${noteVal}`);
        }
        
        vCardLines.push('END:VCARD');

        this.vCardString = vCardLines.join("\r\n");
        this.vCardBlob = new Blob([this.vCardString], { type: 'text/vcard;charset=utf-8' });
        this.vCardFilename = `${this.addressData.gDisp_1.replace(/[\\\/:*?"<>|,. ]/g, "_") }_contact.vcf`;

        this.enableButton("download");
        this.enableButton("sendEmail");
    }

    private _onChange_Data() {

        this.generateVCard();
    }

    private downloadVCard() {
        
        let url = URL.createObjectURL(this.vCardBlob);

        let link = document.createElement('a');

        link.href = url;        

        link.download = this.vCardFilename;

        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    private sendVCardByEmail() {
        this.StandbyStart();

        let file = new File([this.vCardBlob], this.vCardFilename, { type: "text/vcard" });

        let reader: FileReader = new FileReader();

        reader.onerror = (error: any) => { this._showErrorMessage(error); };

        reader.onload = () => {
            let base64Content = <string>reader.result;
            this.StandbyStop();
            let mailEditor = new emailForm();

            mailEditor.setMailFormContext({
                isHistoryContext: true,
                contextName: "vCardGenerator",
            });

            mailEditor.show("dlg", `vCard - ${this.addressData.gDisp_1}`, this.getTabContainerName());
            mailEditor.addAttachment(this.vCardFilename, null, 'rawFile', base64Content);
            mailEditor.setSubject(this.translate("AddressWizard__Label_ShareVCard"));

            this.close();
        };

        reader.readAsDataURL(file);
    }

    private _showErrorMessage(err?: more.requestErrorMessage | string) {
        let msg;

        if (typeof err == "string") msg = err;
        else msg = err ? err.Message : "...error occured";

        this.StandbyStop();
        this.showToast(msg, "fas fa-exclamation-triangle");
    }

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this.show("dlg");
        this.addButton("download", this.translate("General_Label_Download"), lang.hitch(this, this.downloadVCard), null, null, "fas fa-download", true, "primary", false);
        this.addButton("sendEmail", this.translate("General_SendByEmail"), lang.hitch(this, this.sendVCardByEmail), null, null, "fas fa-envelope-open-text", false, "primary", false);

        this.loadAddressData();
    }

    startup(): void {
        super.startup.apply(this, arguments as any);
    }
}

export default mdsWidget(VCardGenerator);